import Debug from '../../helpers/debug.js';
import ora from 'ora';
import AWS from 'aws-sdk';
import Table from 'cli-table';
import descriptions from '../../helpers/descriptions.js';
import { pr, themed, cl, printJSON } from '../../helpers/print.js';
import { genCredentialsOnClientApi } from '../../helpers/api.js';
import { getEntityLabel } from '../../helpers/flow.js';

const debug = Debug('commands:etl:delete');

const command = 'delete';

const desc = 'Delete ETL scripts';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('flow', descriptions.options['flow'].config)
    .demandOption('flow', descriptions.options['flow'].demandText)
    .option('tap', descriptions.options['tap'].config)
    .demandOption('tap', descriptions.options['tap'].demandText)
    .option('tenant', { ...descriptions.options['tenant'].config })
    .demandOption('tenant', descriptions.options['tenant'].demandText);
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, flow, tap, tenant } = argv;
  const { clientApiBaseUri } = hg;

  let message;
  let spinner = ora();
  try {
    message = themed(
      `Deleting ETL scripts for Tenant ${themed(tenant, 'info')} Flow ${themed(flow, 'info')} and Tap ${themed(
        tap,
        'info'
      )} to ${themed(env, 'info')}`
    );
    !json && spinner.info(themed(`Info: ${message}.`, 'secondary'));
    // 1. get STS credentials
    message = themed(`Verifying user and authorizing`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const { accessKeyId, secretAccessKey, sessionToken } = await genCredentialsOnClientApi({
      debug,
      baseUri: clientApiBaseUri,
      task: 'etl-deploy',
      env,
      tenant,
      flow,
      tap,
      apikey,
    });
    // created authenticated S3 instance
    const s3 = new AWS.S3({
      accessKeyId,
      secretAccessKey,
      sessionToken,
    });
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    // 2. delete ETL folder
    message = themed(`Deleting ETL scripts`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const keyPrefix = `${tenant}/flows/${flow}/${await getEntityLabel(s3, env, tenant, flow)}/${tap}/etl/`;
    // 2.1 Get all objects from folder
    const listObjectsRes = await s3.listObjectsV2({ Bucket: env, Prefix: keyPrefix }).promise();
    debug('s3-list-objects-res', listObjectsRes);
    if (listObjectsRes.Contents?.length > 0) {
      // 2.2 Delete all objects
      const deleteObjectsRes = await s3
        .deleteObjects({
          Bucket: env,
          Delete: {
            Objects: listObjectsRes.Contents.map((object) => {
              return { Key: object.Key };
            }),
          },
        })
        .promise();
      debug('s3-list-objects-res', deleteObjectsRes);
    }
    // 2.3 Delete the folder if needed
    const response = await s3.deleteObject({ Bucket: env, Key: keyPrefix }).promise();
    debug('s3-delete-object-res', response);
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
    if (listObjectsRes.Contents?.length > 0) {
      const table = new Table({ head: ['File', 'Status'] });
      listObjectsRes.Contents.filter(({ Key }) => {
        return Key !== keyPrefix;
      }).forEach(({ Key }) => {
        const splittedKey = Key.split('/');
        const filename = splittedKey[splittedKey.length - 1];
        table.push([filename, themed('Deleted', 'info')]);
      });
      if (json) {
        printJSON({
          status: 'success',
          deletedObjects: (listObjectsRes.Contents ?? []).map(({ Key }) => Key),
        });
      } else {
        cl(table.toString());
      }
    } else {
      message = themed(`There was no file or folder to be deleted`);
      json
        ? printJSON({ status: 'success', deletedObjects: [] })
        : spinner.info(themed(`Info: ${message}.`, 'secondary'));
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err });
    } else {
      spinner.fail(themed(`Error: ${message}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
      if (err && err.response && err.response.data) {
        debug('response', err.response.data);
      }
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };
